import { useEffect, useState } from 'react';
import axiosInstance from '../../api/axiosConfig';
import { hasCardAction } from '../../utils/permissionHelper';

const CARD = 'sales-target-management';

/**
 * May this user perform `action` on the Sales Target card?
 *
 * Mirrors the server: requireCard lets system_admin through unconditionally,
 * and a system_admin need not carry the card in permissions.cards — so the
 * plain hasCardAction check would hide buttons the server would accept.
 * Pass several actions to mean "any of them" (a PUT is accepted on edit OR
 * approve).
 */
export const canSalesTarget = (user, ...actions) => {
    if (!user) return false;
    if (user.role === 'system_admin') return true;
    return actions.some(action => hasCardAction(user.permissions, CARD, action));
};

// Before the server answers — and if an older backend has no /scope route —
// behave as the pages did before: role decides, sales_manager opens on a branch.
const legacyScope = (user) => (
    user?.role === 'sales_manager'
        ? { unrestricted: false, branchIds: null, home: { level: 'branch', organizationId: null, name: null }, legacy: true }
        : { unrestricted: true, branchIds: null, home: { level: 'root', organizationId: null, name: null }, legacy: true }
);

/**
 * The caller's Sales Target scope as the server resolved it
 * (GET /api/sales-target/scope): `unrestricted`, `branchIds`, and `home` — the
 * level + organization a page should open on.
 *
 * Pages open from this instead of guessing from the role name, which stopped
 * matching the server once scope came from the user's tenant: a branch user
 * of any role other than sales_manager used to open on the whole group.
 *
 * @returns {null | {unrestricted, branchIds, home: {level, organizationId, name}}}
 *          null while loading.
 */
export const useSalesScope = (user) => {
    const [scope, setScope] = useState(null);

    useEffect(() => {
        let cancelled = false;
        axiosInstance.get('/api/sales-target/scope')
            .then(res => {
                if (cancelled) return;
                setScope(res.data?.success ? res.data.data : legacyScope(user));
            })
            .catch(() => { if (!cancelled) setScope(legacyScope(user)); });
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return scope;
};
